import React, { useMemo, useState } from 'react'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useSearchDealsByCompanyQuery, useSearchDealsQuery } from '@paintscout/api'
import type { Deal, SearchDealsByCompanyQuery, SearchDealsQuery } from '@paintscout/api'
import { Sentry } from 'src/util/appSentry'
import { useSnackbar } from 'notistack'
import DealTable from './DealTable'

const useStyles = makeStyles<Theme>(
  (_theme) => ({
    root: {}
  }),
  { name: 'DealDocList' }
)

export interface DealDocListProps {
  classes?: StyleClasses<typeof useStyles>
  companyId?: string
  query?: string
  limit?: number
}

const DealDocList = ({ companyId, query, limit: argsLimit = 5 }: DealDocListProps) => {
  const { enqueueSnackbar } = useSnackbar()
  const limit = argsLimit

  const classes = useStyles()
  const {
    data: dealsData,
    error: dealsError,
    loading: fetchingDealsData,
    fetchMore: fetchMoreDeals,
    updateQuery: updateDealsQuery
  } = useSearchDealsQuery({
    variables: { query, limit },
    fetchPolicy: 'cache-and-network',
    skip: !query
  })

  const {
    data: dealsByCompanyData,
    loading: fetchingByCompany,
    error: dealsByCompanyError,
    fetchMore: fetchMoreDealsByCompany,
    updateQuery: updateDealsByCompanyQuery
  } = useSearchDealsByCompanyQuery({
    variables: { companyId, limit },
    fetchPolicy: 'cache-and-network',
    skip: !companyId
  })

  const { deals, totalRows } = useMemo(() => {
    return {
      deals: dealsByCompanyData?.searchDealsByCompany?.deals ?? dealsData?.searchDeals?.deals ?? [],
      totalRows: dealsByCompanyData?.searchDealsByCompany?.total_rows ?? dealsData?.searchDeals?.total_rows ?? 0
    }
  }, [dealsByCompanyData, dealsData])
  const loading = useMemo(() => fetchingByCompany || fetchingDealsData, [fetchingByCompany, fetchingDealsData])
  const [page, setPage] = useState(1)

  if (dealsByCompanyError || dealsError) {
    Sentry.captureException(dealsByCompanyError || dealsError, {
      tags: { companyId, query }
    })
    enqueueSnackbar('Unable to load Deals', { variant: 'error' })
  }

  const handleFetchNextPage = () => {
    if (companyId) {
      fetchMoreDealsByCompany({
        variables: {
          limit: limit,
          from: page * limit,
          bookmark: dealsByCompanyData?.searchDealsByCompany?.bookmark
        },
        updateQuery: (prev: SearchDealsByCompanyQuery, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return Object.assign({}, prev, {
            searchDealsByCompany: {
              ...prev.searchDealsByCompany,
              deals: [...prev.searchDealsByCompany.deals, ...fetchMoreResult.searchDealsByCompany.deals],
              bookmark: fetchMoreResult.searchDealsByCompany.bookmark
            }
          })
        }
      })
    } else {
      fetchMoreDeals({
        variables: {
          limit: limit,
          from: page * limit,
          bookmark: dealsData?.searchDeals?.bookmark
        },
        updateQuery: (prev: SearchDealsQuery, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return Object.assign({}, prev, {
            searchDeals: {
              ...prev.searchDeals,
              deals: [...prev.searchDeals.deals, ...fetchMoreResult.searchDeals.deals],
              bookmark: fetchMoreResult.searchDeals.bookmark
            }
          })
        }
      })
    }
  }

  function removeRowsFromQuery(ids: string[]) {
    if (companyId) {
      updateDealsByCompanyQuery((cache: SearchDealsByCompanyQuery) => {
        if (!cache.searchDealsByCompany || !cache.searchDealsByCompany.deals) {
          return cache
        }
        return {
          ...cache,
          searchDealsByCompany: {
            ...cache.searchDealsByCompany,
            deals: cache.searchDealsByCompany.deals.filter((deal) => !ids.includes(deal._id)),
            total_rows: cache.searchDealsByCompany.total_rows - ids.length
          }
        }
      })
    } else {
      updateDealsQuery((cache: SearchDealsQuery) => {
        if (!cache.searchDeals || !cache.searchDeals.deals) {
          return cache
        }
        // Filter out the deals to be removed
        return {
          ...cache,
          searchDeals: {
            ...cache.searchDeals,
            deals: cache.searchDeals.deals.filter((deal) => !ids.includes(deal._id)),
            total_rows: cache.searchDeals.total_rows - ids.length
          }
        }
      })
    }
  }

  function updateRowsInQuery(deal: Deal) {
    if (companyId) {
      updateDealsByCompanyQuery((cache: SearchDealsByCompanyQuery) => {
        if (!cache.searchDealsByCompany) return cache

        return {
          ...cache,
          searchDealsByCompany: {
            ...cache.searchDealsByCompany,
            deals: cache.searchDealsByCompany.deals.map((d) => (d._id === deal._id ? deal : d))
          }
        }
      })
    } else {
      updateDealsQuery((cache: SearchDealsQuery) => {
        if (!cache.searchDeals) return cache

        return {
          ...cache,
          searchDeals: {
            ...cache.searchDeals,
            deals: cache.searchDeals.deals.map((d) => (d._id === deal._id ? deal : d))
          }
        }
      })
    }
  }

  return (
    <div className={classes.popover}>
      <DealTable
        dense
        actions={{
          delete: true,
          edit: true,
          archive: true
        }}
        deals={deals.map((deal) => ({
          ...deal,
          id: deal._id
        }))}
        skipRefetchQueries
        loading={loading}
        totalRows={totalRows}
        page={page}
        rowsPerPage={limit}
        pages={Math.ceil(totalRows / limit)}
        onPageChange={(e) => {
          setPage(e)
        }}
        onFetchNextPage={handleFetchNextPage}
        onDelete={removeRowsFromQuery}
        onArchive={removeRowsFromQuery}
        onEdit={updateRowsInQuery}
        disablePagination={totalRows <= limit}
      />
    </div>
  )
}

export default DealDocList
